import { TokenForm } from "@/components/token/TokenForm";
import { SimpleSEO } from '@/components/seo/SimpleSEO';
import { Logo } from "@/components/layout/Logo";
import { 
  CheckCircle2, 
  ShieldCheck, 
  Fuel,
  BookOpen,
  Rocket,
  RefreshCw,
  Droplet,
  Flame,
  Wrench,
  Shield,
  Wallet,
  Search,
  Lock,
  CheckCircle,
  BarChart3Icon,
  LockIcon,
  FileCode2,
  HelpCircle,
  Wifi
} from 'lucide-react';
import { Suspense } from 'react';
import { useEffect, useState } from 'react';
import { useNetwork, useAccount, usePublicClient } from 'wagmi';
import { useTheme } from "@/providers/ThemeProvider";
import AppLoadingScreen from '@/components/AppLoadingScreen';

export function CreateTokenPage() {
  const { chain } = useNetwork();
  const { } = useAccount();
  const publicClient = usePublicClient();
  const [latency, setLatency] = useState<number | null>(null);
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const handleLoadingComplete = () => setIsLoading(false);
  const [buttonLayout, setButtonLayout] = useState<'Top' | 'Bottom'>(
    (localStorage.getItem('buttonLayout') as 'Top' | 'Bottom') || 'Top'
  );

  useEffect(() => {
    const checkLatency = async () => {
      if (!publicClient) return;
      
      try {
        const start = Date.now();
        await publicClient.getBlockNumber();
        const end = Date.now();
        setLatency(end - start);
      } catch (error) {
        console.error("Failed to check latency:", error);
        setLatency(null);
      }
    };

    const interval = setInterval(checkLatency, 10000); // Check every 10 seconds
    checkLatency(); // Initial check

    return () => clearInterval(interval);
  }, [publicClient]);

  // Get token standard and network name based on chain
  const getTokenInfo = () => {
    if (chain?.id === 56 || chain?.id === 97 || chain?.id === 204 || chain?.id === 5611) {
      return {
        standard: 'BEP20',
        network: chain?.id === 204 || chain?.id === 5611 ? 'opBNB' : 'BSC',
        networkFull: chain?.id === 204 ? 'opBNB Mainnet' : chain?.id === 5611 ? 'opBNB Testnet' : 'Binance Smart Chain'
      };
    }
    if (chain?.id === 1 || chain?.id === 11155111) {
      return {
        standard: 'ERC20',
        network: 'ETH',
        networkFull: 'Ethereum'
      };
    }
    if (chain?.id === 43114 || chain?.id === 43113) {
      return {
        standard: 'ERC20',
        network: 'AVAX',
        networkFull: 'Avalanche'
      };
    }
    if (chain?.id === 11124) {
      return {
        standard: 'ERC20',
        network: 'Abstract',
        networkFull: 'Abstract Testnet'
      };
    }
    if (chain?.id === 2741) {
      return {
        standard: 'ERC20',
        network: 'Abstract',
        networkFull: 'Abstract Mainnet'
      };
    }
    if (chain?.id === 8453) {
      return {
        standard: 'ERC20',
        network: 'Base',
        networkFull: 'Base'
      };
    }
    if (chain?.id === 84532) {
      return {
        standard: 'ERC20',
        network: 'Base',
        networkFull: 'Base Sepolia'
      };
    }
    if (chain?.id === 10) {
      return {
        standard: 'ERC20',
        network: 'OP',
        networkFull: 'OP Mainnet'
      };
    }
    if (chain?.id === 11155420) {
      return {
        standard: 'ERC20',
        network: 'OP',
        networkFull: 'OP Sepolia'
      };
    }
    if (chain?.id === 42161) {
      return {
        standard: 'ERC20',
        network: 'Arbitrum',
        networkFull: 'Arbitrum One'
      };
    }
    if (chain?.id === 421614) {
      return {
        standard: 'ERC20',
        network: 'Arbitrum',
        networkFull: 'Arbitrum Sepolia'
      };
    }
    if (chain?.id === 137) {
      return {
        standard: 'ERC20',
        network: 'Polygon',
        networkFull: 'Polygon'
      };
    }
    if (chain?.id === 80002) {
      return {
        standard: 'ERC20',
        network: 'Polygon',
        networkFull: 'Polygon Amoy Testnet'
      };
    }
    if (chain?.id === 1328) {
      return {
        standard: 'ERC20',
        network: 'Sei',
        networkFull: 'Sei Atlantic-2 Testnet'
      };
    }
    if (chain?.id === 1329) {
      return {
        standard: 'ERC20',
        network: 'Sei',
        networkFull: 'Sei Pacific-1 Mainnet'
      };
    }
    // Default to BSC
    return {
      standard: 'BEP20',
      network: 'BSC',
      networkFull: 'Binance Smart Chain'
    };
  };

  const tokenInfo = getTokenInfo();

  useEffect(() => {
    const handler = (e: CustomEvent) => {
      if (e.detail === 'Top' || e.detail === 'Bottom') setButtonLayout(e.detail);
    };
    window.addEventListener('buttonLayoutChanged', handler as EventListener);
    return () => window.removeEventListener('buttonLayoutChanged', handler as EventListener);
  }, []);

  useEffect(() => {
    console.log('CreateTokenPage mounted');
    return () => console.log('CreateTokenPage unmounted');
  }, []);

  

  const title = "Create Your Token";
  const description = "Launch your own ERC20/BEP20 token with advanced features. Set limits, configure tokenomics, and deploy securely.";
  const keywords = "create token, token generator, ERC20, BEP20, tokenomics, crypto launch";

  const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
    const [isOpen, setIsOpen] = useState(false);

    const hoverBgColor = theme === 'dark' ? 'hover:bg-blue-900/10' : 'hover:bg-blue-50/50';
    const textColor = theme === 'dark' ? 'text-gray-200' : 'text-gray-900';

    return (
      <div className="border-b last:border-b-0" style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}>
        <button
          className={`w-full py-5 px-4 flex justify-between items-center ${hoverBgColor} transition-colors duration-200`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={`text-lg font-medium ${textColor}`}>{question}</span>
          <span className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} style={{
            color: `var(--color-secondary, #10b981)`
          }}>
            ▼
          </span>
        </button>
        <div
          className={`overflow-hidden transition-all duration-200 ${
            isOpen ? 'max-h-96 py-4 px-4' : 'max-h-0'
          }`}
        >
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>{answer}</p>
        </div>
      </div>
    );
  };

  const faqData = [
    {
      question: "What is MdbApp Token Creator?",
      answer: "MdbApp Token Creator is a professional platform for creating and deploying ERC20 and BEP20 tokens across multiple blockchains including Ethereum and BNB Smart Chain. Our platform simplifies the token creation process with a user-friendly interface."
    },
    {
      question: "What are ERC-20 and BEP-20 Tokens?",
      answer: "ERC-20 and BEP-20 are token standards for the Ethereum and BNB Smart Chain networks respectively. These tokens are blockchain-based assets that can be sent, received, and stored like any cryptocurrency, following specific protocol standards for compatibility and security."
    },
    {
      question: "What types of tokens can I create?",
      answer: "You can create several types of tokens: Taxable tokens with configurable tax rates, Dividend tokens that reward holders, Deflationary tokens with burn mechanisms, Fixed Supply tokens, Capped Supply tokens, and Unlimited Supply tokens. Each type serves different project needs and use cases."
    },
    {
      question: "What are my total supply options?",
      answer: "We offer Fixed Supply (unchangeable total supply) The initial supply is sent to the owner's wallet upon creation."
    },
    {
      question: "Can I modify token settings after deployment?",
      answer: "Yes, you can use token tools to adjust/modify your token settings such as Tax Rates, Transfering ownership ETC, And you can use the Smart Contract locker to Lock specific features, Master Lock the contract, Or even renounce ownership."
    },
    {
      question: "Who can Burn Tokens?",
      answer: "Anyone can burn tokens if they have the token in thier wallet. This is not restricted to a contract owner. You can use token tools to burn tokens. Simply select Token tools, enter the tokens address, and select ammount to burn. This is a contract level burn and will reflect immediately across the blockchain."
    }
  ];

  return (
    <>
      {isLoading && <AppLoadingScreen onComplete={handleLoadingComplete} />}
      <SimpleSEO
        title={title}
        description={description}
        keywords={keywords}
      />
      <article className="max-w-3xl mx-auto space-y-4">
        <header className={`text-center space-y-4 ${
          buttonLayout === 'Bottom' 
            ? 'pt-4 sm:pt-6 md:pt-8' 
            : ''
        }`}>
          <div className="flex items-center justify-center gap-2 sm:gap-4">
            <div className="matrix-logo-left shrink-0">
              <Logo className="w-8 h-8 sm:w-10 sm:h-10" showRocket />
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold bg-clip-text text-transparent" style={{
              backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`
            }}>
              Create Your {tokenInfo.standard} Token on {tokenInfo.network}
            </h1>
            <div className="matrix-logo-right shrink-0">
              <Logo className="w-8 h-8 sm:w-10 sm:h-10" showRocket />
            </div>
          </div>
          <p className="text-xl text-muted-foreground">
            Launch your own cryptocurrency token on {tokenInfo.networkFull} in minutes
          </p>
          <div className="flex justify-center space-x-6 text-sm">
            <span className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#0A74FF]/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 [color:#22C55E]" />
              </div>
              <span className="text-muted-foreground">Verified Contract</span>
            </span>
            <span className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#0A74FF]/10 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 [color:#0A74FF]" />
              </div>
              <span className="text-muted-foreground">Secure & Audit-Ready</span>
            </span>
            <span className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#0A74FF]/10 flex items-center justify-center">
                <Fuel className="w-5 h-5 [color:#F97316]" />
              </div>
              <span className="text-muted-foreground">Optimized Gas Fees</span>
            </span>
          </div>
        </header>

        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold tracking-tight">
                Create Your Token
              </h1>
            </div>

            {/* Network Status */}
            <div className="relative rounded-lg mb-6 border-2 border-[var(--color-header-accent,#0A74FF)]">
              <div className="relative bg-background/30 backdrop-blur-sm rounded-lg">
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg text-foreground">Network Status</span>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Wifi className="h-4 w-4 text-green-500" />
                      <span className="text-green-500">Healthy</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Network:</span>
                      <span className="text-foreground">{chain?.name || "Not Connected"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Latency:</span>
                      <span className="text-foreground">{latency ? `${latency}ms` : "---"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Token Creation Form */}
            <Suspense fallback={<div>Loading token form...</div>}>
              <TokenForm />
            </Suspense>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-6 bg-clip-text text-transparent" style={{
              backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`
            }}>Frequently Asked Questions</h2>
            <div className="relative rounded-lg border-2 border-[var(--color-header-accent,#0A74FF)]">
              <div className="relative bg-background/30 backdrop-blur-sm rounded-lg">
                <div className="p-6">
                  <div className="space-y-4">
                    {faqData.map((faq, index) => (
                      <FAQItem key={index} question={faq.question} answer={faq.answer} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Guides & Blog Section */}
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Guides & Blog</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Create Token Guide */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/create-token-guide";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-yellow-500 hover:bg-yellow-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-5 h-5 text-yellow-500" />
                    <span>Create Token Guide</span>
                  </div>
                </div>
              </div>

              {/* Create Meme Coin Guide */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/create-meme-coin";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-orange-500 hover:bg-orange-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <Rocket className="w-5 h-5 text-orange-500" />
                    <span>Create Meme Coin Guide</span>
                  </div>
                </div>
              </div>

              {/* Create Reflection Token */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/create-reflection-token";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-green-500 hover:bg-green-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <RefreshCw className="w-5 h-5 text-green-500" />
                    <span>Create Reflection Token</span>
                  </div>
                </div>
              </div>

              {/* Create Auto-Liquidity Token */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/create-auto-liquidity-token";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-blue-500 hover:bg-blue-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <Droplet className="w-5 h-5 text-blue-500" />
                    <span>Create Auto-Liquidity Token</span>
                  </div>
                </div>
              </div>

              {/* Create Auto-Burn Token */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/create-auto-burn-token";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-red-500 hover:bg-red-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <Flame className="w-5 h-5 text-red-500" />
                    <span>Create Auto-Burn Token</span>
                  </div>
                </div>
              </div>

              {/* Matrix Contract Scanner */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/matrix-contract-scanner";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-emerald-500 hover:bg-emerald-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <Search className="w-5 h-5 text-emerald-500" />
                    <span>Matrix Contract Scanner</span>
                  </div>
                </div>
              </div>

              {/* Token Tools Guide */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/token-tools";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-indigo-500 hover:bg-indigo-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <Wrench className="w-5 h-5 text-indigo-500" />
                    <span>Token Tools Guide</span>
                  </div>
                </div>
              </div>

              {/* Contract Locker Guide */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/contract-locker";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-amber-500 hover:bg-amber-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-amber-500" />
                    <span>Contract Locker Guide</span>
                  </div>
                </div>
              </div>

              {/* Contract Verification */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/automatic-contract-verification";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-teal-500 hover:bg-teal-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-500" />
                    <span>Contract Verification</span>
                  </div>
                </div>
              </div>

              {/* Smart Contract Locking Guide */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/smart-contract-locking";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-cyan-500 hover:bg-cyan-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <LockIcon className="w-5 h-5 text-cyan-500" />
                    <span>Smart Contract Locking</span>
                  </div>
                </div>
              </div>

              {/* Token Economics Guide */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/token-economics";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-violet-500 hover:bg-violet-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <BarChart3Icon className="w-5 h-5 text-violet-500" />
                    <span>Token Economics Guide</span>
                  </div>
                </div>
              </div>

              {/* Token Security Guide */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/token-security";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-purple-500 hover:bg-purple-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-purple-500" />
                    <span>Token Security Guide</span>
                  </div>
                </div>
              </div>

              {/* Add Token Logo Guide */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/add-token-logo";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-pink-500 hover:bg-pink-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <FileCode2 className="w-5 h-5 text-pink-500" />
                    <span>Add Token Logo Guide</span>
                  </div>
                </div>
              </div>

              {/* Best Cold Storage Wallets Guide */}
              <div onClick={(e) => {
                e.preventDefault();
                window.location.href = "/blog/best-cold-storage-wallets";
              }} className="group block cursor-pointer">
                <div className={`p-4 rounded-lg border border-sky-500 hover:bg-sky-500/10 transition-colors duration-200`}>
                  <div className="flex items-center gap-3">
                    <Wallet className="w-5 h-5 text-sky-500" />
                    <span>Best Cold Storage Wallets</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
